import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function ProtectedRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show professional loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground animate-pulse">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the intended destination for redirect after login
    // Don't preserve login/signup/reset-password routes to avoid loops
    const currentPath = location.pathname + location.search;
    const shouldPreserveRedirect =
      !currentPath.startsWith('/login') &&
      !currentPath.startsWith('/signup') &&
      !currentPath.startsWith('/reset-password');

    const loginUrl = shouldPreserveRedirect
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : '/login';

    return <Navigate to={loginUrl} replace />;
  }

  // State B: Authenticated but email not verified
  // Redirect to verification notice unless already on verification routes
  const currentPath = location.pathname;
  const isOnVerificationRoute =
    currentPath.startsWith('/verify-email') || currentPath.startsWith('/verify-complete');

  // Compute verified status: emailVerified OR has OAuth provider
  // Firebase may reset emailVerified to false when password is linked to OAuth account
  // Google/Microsoft users are verified by definition (OAuth providers verify email)
  const isVerified =
    user &&
    (user.emailVerified ||
      user.providerData.some(
        (p) => p.providerId === 'google.com' || p.providerId === 'microsoft.com'
      ));

  if (user && !isVerified && !isOnVerificationRoute) {
    return <Navigate to="/verify-email" replace />;
  }

  return <Outlet />;
}
